import { createSlice } from '@reduxjs/toolkit';
import { IAgsExport, IAgsExportRevision } from 'Data/ViewModels/Ags';
import { ListItemModel } from 'Data/ViewModels/ListItemModel';
import { format, FormatTypes } from 'Lib/Format';

import {
  IInitAction,
  IInitResolveAction,
  IProjectState,
  IReissueExportAction,
  IReissueExportResolveAction,
  IRunNewExportAction,
  IRunNewExportResolveAction,
  ISetAgsExportConfigurationAction,
  ISetAgsExportConfigurationResolveAction,
} from './types';

const initialState: IProjectState = {
  projectId: undefined,
  projectDetails: {
    projectCode: undefined,
    projectName: undefined,
    clientName: undefined,
    agsExportConfigurationId: undefined,
    agsExportConfigurationName: undefined,
    laboratoryMappedAgsExportConfigurationName: undefined,
  },
  pageHeader: {
    identifier: '',
    shortName: 'AGS Data',
    description: '',
    status: 'Saved',
    processing: false,
  },
  historyDateRangeSelectList: [],
  exportHistory: [],
  status: {
    ready: false,
    errorMessage: undefined,
    busyState: false,
  },
};

const slice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    init: (state, action: IInitAction) => {
      state.status.ready = false;
      state.status.errorMessage = undefined;
      state.status.busyState = false;

      state.projectId = action.payload;
    },
    runNewExport: (state, action: IRunNewExportAction) => {
      state.status.busyState = true;
    },
    reissueExport: (state, action: IReissueExportAction) => {
      state.status.busyState = true;
    },
    initResolve: (state, action: IInitResolveAction) => {
      const project = action.payload.Project;
      if (!project) return;

      state.projectDetails = {
        projectCode: project.Code,
        projectName: project.Name,
        clientName: project.ClientName,
        agsExportConfigurationId: project.AgsExportConfigurationUUID,
        agsExportConfigurationName: project.AgsExportConfigurationName,
        laboratoryMappedAgsExportConfigurationName: project.LaboratoryMappedAgsExportConfigurationName,
      };

      state.pageHeader.identifier = project.Code;
      state.pageHeader.description = 'AGS Data for ' + project.Name;

      const agsExports = action.payload.AgsExports.sort(compareAgsExportByDate);
      state.historyDateRangeSelectList = agsExports.map(convertAgsExportToListItem);

      state.exportHistory = agsExports.flatMap((agsExport) =>
        agsExport.Revisions.map((revision) => prepareSingleHistoryRow(agsExport, revision))
      );

      state.status.ready = true;
    },
    initReject: (state) => {
      state.status.errorMessage = 'The Project could not be found, please check that the ID is correct.';
      state.pageHeader.identifier = '';
      state.pageHeader.description = '';
      state.status.ready = true;
    },
    runNewExportResolve: (state, action: IRunNewExportResolveAction) => {
      const agsExport = action.payload;
      const latestRevision = action.payload.Revisions[action.payload.Revisions.length - 1];
      state.exportHistory = sortExportHistory([
        ...state.exportHistory,
        prepareSingleHistoryRow(agsExport, latestRevision),
      ]);

      if (!state.historyDateRangeSelectList.some((x) => x.value === agsExport.Id)) {
        state.historyDateRangeSelectList.push(convertAgsExportToListItem(agsExport));
      }
      state.status.busyState = false;
    },
    runNewExportReject: (state) => {
      state.status.busyState = false;
    },
    reissueExportResolve: (state, action: IReissueExportResolveAction) => {
      const revision = action.payload;
      const existingHistoryRow = state.exportHistory.find((x) => x.AgsExportId === revision.AgsExportId);

      //Build an IAgsExport object since the API call just returns the newly created revision.
      //This is a kind of hacky workaround for us not storing the complete IAgsExports response
      //anywhere, which we probably should do (and then we can update the model by adding the
      //new revision to the corresponding IAgsExport model in the list.
      const agsExport: IAgsExport = {
        Id: revision.AgsExportId,
        Project: {
          Id: state.projectId ?? '',
          Code: state.projectDetails.projectCode ?? '',
          Name: state.projectDetails.projectName ?? '',
          ClientName: state.projectDetails.clientName ?? '',
          AgsExportConfigurationUUID: state.projectDetails.agsExportConfigurationId,
          AgsExportConfigurationName: state.projectDetails.agsExportConfigurationName ?? '',
          LaboratoryMappedAgsExportConfigurationName:
            state.projectDetails.laboratoryMappedAgsExportConfigurationName ?? '',
        },
        StartDate: existingHistoryRow?.StartDate,
        EndDate: existingHistoryRow?.EndDate,
        WorkOrder: undefined,
        Revisions: [],
      };

      var newHistoryRow = prepareSingleHistoryRow(agsExport, revision);
      state.exportHistory = sortExportHistory([...state.exportHistory, newHistoryRow]);
      state.status.busyState = false;
    },
    reissueExportReject: (state) => {
      state.status.busyState = false;
    },
    setAgsExportConfiguration: (state, action: ISetAgsExportConfigurationAction) => {
      state.pageHeader.processing = true;
      state.pageHeader.status = 'Saving...';
    },
    setAgsExportConfigurationReject: (state) => {
      state.pageHeader.processing = false;
      state.pageHeader.status = 'Saved';
    },
    setAgsExportConfigurationResolve: (state, action: ISetAgsExportConfigurationResolveAction) => {
      const project = action.payload;

      state.projectDetails = {
        projectCode: project.Code,
        projectName: project.Name,
        clientName: project.ClientName,
        agsExportConfigurationId: project.AgsExportConfigurationUUID,
        agsExportConfigurationName: project.AgsExportConfigurationName,
        laboratoryMappedAgsExportConfigurationName: project.LaboratoryMappedAgsExportConfigurationName,
      };

      state.pageHeader.processing = false;
      state.pageHeader.status = 'Saved';
    },
  },
});

const prepareSingleHistoryRow = (agsExport: IAgsExport, revision: IAgsExportRevision): IAgsExportRevision => ({
  Id: revision.Id,
  AgsExportId: revision.AgsExportId,
  StartDate: agsExport.StartDate,
  EndDate: agsExport.EndDate,
  IssueNumber: revision.IssueNumber,
  CreationDate: revision.CreationDate,
  Filename: revision.Filename,
  FileSizeBytes: revision.FileSizeBytes,
  IsContentValid: revision.IsContentValid,
  AgsExportConfigurationName: revision.AgsExportConfigurationName,
});

const convertAgsExportToListItem = (agsExport: IAgsExport): ListItemModel => ({
  label:
    format({ type: FormatTypes.Date })(agsExport.StartDate) +
    ' - ' +
    format({ type: FormatTypes.Date })(agsExport.EndDate),
  value: agsExport.Id,
});

const compareAgsExportByDate = (x: IAgsExport, y: IAgsExport) => {
  if (x.StartDate && y.StartDate && x.StartDate < y.StartDate) return -1;
  if (x.StartDate && y.StartDate && x.StartDate > y.StartDate) return 1;
  if (x.EndDate && y.EndDate && x.EndDate < y.EndDate) return -1;
  if (x.EndDate && y.EndDate && x.EndDate > y.EndDate) return 1;
  return 0;
};

const sortExportHistory = (data: IAgsExportRevision[]): IAgsExportRevision[] => {
  data.sort(function (x, y) {
    let xKey = x.StartDate + '_' + x.EndDate + '_' + ('00' + x.IssueNumber).slice(-3);
    let yKey = y.StartDate + '_' + y.EndDate + '_' + ('00' + y.IssueNumber).slice(-3);

    if (xKey < yKey) {
      return -1;
    }
    if (xKey > yKey) {
      return 1;
    }
    return 0;
  });

  return data;
};

export const { actions, reducer } = slice;
export default slice.reducer;
